import { StemType } from '@arpeggi/shared'
import { CSSProperties } from '@stitches/react'
import { observer } from 'mobx-react-lite'
import { FC, forwardRef } from 'react'
import { GRAY_COLORS, LOOP_COLORS } from '../../lib/stitches'
import { useTreeState } from '../../stores/Root'
import { ISongStore } from '../../stores/song/SongStore'
import { IStemStore } from '../../stores/song/StemStore'
import { Column, VerticalCenteredRow } from '../core/Components'
import { getClipColor } from '../platform/color'
import { useMidi } from '../studio/Midi'
import STCControls from './controls/STCControls'
import useSelectedTrackContainer from './hooks/useSelectedTrackContainer'
import STCBody from './selectedcontainer/STCBody'
import STCLabel from './selectedcontainer/STCLabel'
import STCStemDetails from './selectedcontainer/STCStemDetails'
import { getStemDisplayData } from './StudioUtils'

export const SelectedTrackContainerTabs = {
  pianoRoll: 0,
  instrument: 1
}

export const DrawerHandle = forwardRef((props, ref) => (
  <div
    className="drawer-handle"
    style={{ position: 'absolute', top: '4px', left: 'calc(50% - 64px)' }}
    draggable
    ref={ref as any}
  />
))

interface SelectedTrackContainerContainerProps {
  height: string
  width: string
  padding?: string
  children?: any
  css?: CSSProperties
}
export const SelectedTrackContainerContainer = forwardRef(
  (
    { height, width, padding, children, css }: SelectedTrackContainerContainerProps,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <Column
      css={{
        height,
        minHeight: height,
        alignItems: 'stretch',
        position: 'fixed',
        bottom: '0',
        padding: padding || '20px 45px 40px 45px',
        backgroundColor: GRAY_COLORS.gray01,
        zIndex: 2,
        right: 0,
        width: width,
        borderTop: '1px solid var(--studio-medium)',
        boxShadow: '0px -4px 6px 2px #00000008,0px -12px 16px 4px #00000008',
        overflow: 'hidden',
        userSelect: 'none',
        ...css
      }}
      ref={ref}
      animateAppear
    >
      {children}
    </Column>
  )
)

const StemIcon = ({ fill }) => (
  <svg
    width="29px"
    height="26px"
    viewBox="0 0 29 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="14.5001" width="14.5001" height="6.44448" fill={fill || '#000000'} />
    <rect y="6.44446" width="14.5001" height="6.44448" fill={fill || '#000000'} />
    <rect x="14.5001" y="12.8889" width="14.5001" height="6.44448" fill={fill || '#000000'} />
    <rect y="19.3334" width="14.5001" height="6.44448" fill={fill || '#000000'} />
  </svg>
)

const SelectedTrackContainer: FC<any> = ({ height, close, width, onResize }) => {
  const { studio } = useTreeState()
  const stemIndex = studio.selectedStemIndex
  const { handleRef, selectedTab, setSelectedTab, selectedDrumTrack, setSelectedDrumTrack, drop } =
    useSelectedTrackContainer(stemIndex, onResize, close)

  const song: ISongStore = studio.song
  const stem: IStemStore = song.stems[stemIndex] as IStemStore
  useMidi(stem)

  const loopIndex: number = stem.getLoopIndex(studio.selectedSection) - 1
  const isDrums: boolean = stem.type() === StemType.DRUMS
  const trackColor: string =
    stem.type() === StemType.AUDIO && studio.selectedClipIDs.length > 0
      ? getClipColor(song.getClipByID(studio.selectedClipIDs[0]))
      : LOOP_COLORS[stem?.colorIndex % LOOP_COLORS.length]
  const { category, details, isRemixed } = getStemDisplayData(studio, stemIndex, true)

  return (
    <SelectedTrackContainerContainer height={height} width={width} ref={drop}>
      {(loopIndex > -1 || stem.type() === StemType.AUDIO) && <DrawerHandle ref={handleRef} />}

      <VerticalCenteredRow css={{ paddingBottom: '16px', height: '75px', overflowY: 'visible' }}>
        <STCStemDetails
          isRemixed={isRemixed}
          trackColor={trackColor}
          details={details}
          category={category}
          loopIndex={loopIndex}
        />
        <STCLabel
          stemType={stem.type()}
          isRemixed={isRemixed}
          isDrums={isDrums}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        <STCControls
          close={close}
          stem={stem}
          selectedDrumTrack={selectedDrumTrack}
          isDrums={isDrums}
        />
      </VerticalCenteredRow>

      <STCBody
        stem={stem}
        selectedTab={selectedTab}
        stemIndex={stemIndex}
        selectedDrumTrack={selectedDrumTrack}
        setSelectedDrumTrack={setSelectedDrumTrack}
      />
    </SelectedTrackContainerContainer>
  )
}

export default observer(SelectedTrackContainer)
